import { z } from "zod";
import { ENTITY_TYPES } from "../constants.ts";

/** ISO 8601 UTC. Every `_at` column uses this. */
export const IsoDateTime = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/, "Expected an ISO 8601 UTC timestamp");

/** `YYYY-MM-DD`. Date fields and follow-ups are day-precision. */
export const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");

export const EntityTypeSchema = z.enum(ENTITY_TYPES);

/** REAL column used for fractional indexing. Must stay finite. */
export const PositionSchema = z.number().finite();

/** Spotify ids are base62 — 22 chars in practice, but don't over-constrain. */
export const SpotifyIdSchema = z.string().regex(/^[A-Za-z0-9]{10,40}$/, "Expected a Spotify id");

export const LocalIdSchema = z
  .string()
  .regex(
    /^(?:usr|fld|lpl|gen|fd|vw|gph|ide|icd|rca|rin)_[A-Za-z0-9_-]{8,32}$/,
    "Expected a CoyKoi id",
  );

/** entity_id columns are polymorphic and hold either kind. */
export const EntityIdSchema = z.union([SpotifyIdSchema, LocalIdSchema]);

export const HexColorSchema = z
  .string()
  .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Expected a hex colour");

/**
 * SQLite has no boolean type, so a column arrives as 0/1 while the same field
 * arrives as a real boolean over HTTP. Accepting both lets one schema serve as
 * the single source of truth for a table instead of a row copy and a wire copy.
 */
export const DbBool = z
  .union([z.boolean(), z.literal(0), z.literal(1)])
  .transform((v) => v === true || v === 1);

export const boolToInt = (value: boolean): 0 | 1 => (value ? 1 : 0);

/**
 * A column stored as JSON text. Parses the string form coming out of SQLite and
 * passes an already-decoded value (the HTTP case) straight through. Undecodable
 * text falls through as a string so the inner schema reports the type error
 * rather than a raw SyntaxError escaping.
 */
export function jsonColumn<S extends z.ZodType>(schema: S) {
  return z.preprocess((value) => {
    if (typeof value !== "string") return value;
    if (value === "") return null;
    try {
      return JSON.parse(value) as unknown;
    } catch {
      return value;
    }
  }, schema);
}

/** Serialize for a JSON text column. `null` stays NULL rather than the text "null". */
export function toJsonColumn(value: unknown): string | null {
  return value === null || value === undefined ? null : JSON.stringify(value);
}

export type EntityType = z.infer<typeof EntityTypeSchema>;
